import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import {
  ChevronRightIcon,
  MegaphoneIcon,
  PlusIcon,
  TagsIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import {
  Button,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  TAXONOMY_DATASET_QUERY_KEYS,
  taxonomyDatasetsQueryOptions,
  useCreateTaxonomyDataset,
  useDeleteTaxonomyDataset,
  useDuplicateTaxonomyDataset,
  type TaxonomyDataset,
} from '@/entities/taxonomy-dataset';
import { taxonomiesQueryOptions } from '@/entities/taxonomy';
import {
  taxonomyCampaignsQueryOptions,
  useCreateTaxonomyCampaign,
  useDeleteTaxonomyCampaign,
  useUpdateTaxonomyCampaign,
  type TaxonomyCampaign,
} from '@/entities/taxonomy-campaign';
import { useCurrentUserId, usePermissions } from '@/entities/user';
import { PageTransition } from '@/shared/ui/animations';
import {
  ListPageBody,
  ListPageLayout,
  PageHeader,
  PageHeaderIdentity,
  PageHeaderTitle,
} from '@/shared/ui/layout';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import {
  PreResolvedAvatarCell,
  redesignTableStyles,
  ROW_WITHOUT_FOCUS_RING,
} from '@/shared/ui/data-table';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SearchInput } from '@/shared/ui';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { ROUTES } from '@/shared/config/routes';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { DatasetActionMenu } from './components/DatasetActionMenu/DatasetActionMenu';
import { CampaignActionMenu } from './components/CampaignActionMenu/CampaignActionMenu';
import { EditCampaignModal } from './components/EditCampaignModal/EditCampaignModal';
import { NewTaxonomyModal } from './components/NewTaxonomyModal/NewTaxonomyModal';
import { groupByCampaign } from './lib/groupByCampaign';
import { useExpandedCampaigns } from './lib/useExpandedCampaigns';

const ROW_CLASSES = `h-[72px] ${ROW_WITHOUT_FOCUS_RING}`;

/**
 * Taxonomy Generation, the index.
 *
 * Lists the taxonomies a workspace has built against its published schemas,
 * nested under the campaign each is filed under, with anything uncampaigned
 * under "Ungrouped" at the end.
 *
 * The tree is built from TWO queries — campaigns and datasets — because a
 * campaign with no taxonomies still has to render, and nothing in the dataset
 * list can tell you one exists. Both are unpaginated, so the grouping and the
 * per-campaign counts are pure client-side work with no extra requests.
 *
 * Rendered as interleaved rows in ONE `<tbody>` rather than a table per
 * campaign: monks-ui's `Datatable` is a thin wrapper over native table elements
 * with no nested-row API, and a table per campaign would break column alignment
 * across groups and give each its own scroll region.
 */
export function TaxonomyGenerationList() {
  const { t } = useTranslation('knowledge');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { canManageTaxonomy } = usePermissions();
  const currentUserId = useCurrentUserId();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createConflict, setCreateConflict] = useState<string | null>(null);
  /*
   * Two pieces of state, not one. `deleteTarget` is never cleared: the dialog
   * stays mounted through a ~150ms exit transition, still rendering current
   * props, so nulling the target on close made the title fall back to the
   * generic "Delete taxonomy" and the count to 0 while it faded out.
   */
  const [deleteTarget, setDeleteTarget] = useState<TaxonomyDataset | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Same never-cleared pattern as `deleteTarget`, for the same exit-transition
  // reason: nulling on close blanks the name mid-fade.
  const [campaignDeleteTarget, setCampaignDeleteTarget] = useState<TaxonomyCampaign | null>(null);
  const [campaignDeleteOpen, setCampaignDeleteOpen] = useState(false);
  const [campaignEditTarget, setCampaignEditTarget] = useState<TaxonomyCampaign | null>(null);
  const [campaignEditOpen, setCampaignEditOpen] = useState(false);
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  /*
   * ONE term, handed to both the grouping and the expansion. They disagreed
   * about what a search meant while each derived its own.
   */
  const term = debouncedSearch.trim().toLowerCase();
  const { isExpanded, toggle } = useExpandedCampaigns(term);

  const query = useQuery(taxonomyDatasetsQueryOptions());
  // Only to render each row's schema name and version; the dataset itself
  // carries ids, not labels.
  const schemasQuery = useQuery(taxonomiesQueryOptions());
  const campaignsQuery = useQuery(taxonomyCampaignsQueryOptions());

  /**
   * The schema and the version THIS taxonomy is pinned to.
   *
   * Not the schema's head version. Publishing a new version archives the old
   * one, so reading the head would relabel every existing row as the new number
   * while its records were assembled from the old shape — defeating, on the one
   * screen that shows a version, the whole reason records pin their own.
   *
   * The summary endpoint carries only the head's number, so the pinned number is
   * not knowable here; the id is, and showing the schema name alone is honest
   * where showing the wrong number is not. The detail page, which loads the full
   * version list, shows the number.
   */
  const schemaLabel = useCallback(
    (dataset: TaxonomyDataset) => {
      const schema = (schemasQuery.data ?? []).find((s) => s.id === dataset.taxonomyId);
      return schema ? schema.name : t('taxonomy.generationList.unknownSchema');
    },
    [schemasQuery.data, t],
  );

  const datasets = useMemo<TaxonomyDataset[]>(() => query.data ?? [], [query.data]);

  /**
   * Matches the schema as well as the name.
   *
   * Both are columns on screen, and searching "gama" for a taxonomy whose only
   * visible link to that word is its schema returned nothing.
   */
  const campaigns = useMemo<TaxonomyCampaign[]>(
    () => campaignsQuery.data ?? [],
    [campaignsQuery.data],
  );

  /**
   * Matches the campaign name as well as the taxonomy name and schema.
   *
   * All three are on screen, and searching for a campaign returning nothing
   * while its name is the row above the results reads as a broken search.
   */
  const groups = useMemo(
    () =>
      groupByCampaign({
        campaigns,
        datasets,
        term,
        schemaLabel,
      }),
    [campaigns, datasets, term, schemaLabel],
  );

  const create = useCreateTaxonomyDataset({
    onSuccess: (created) => {
      setCreateOpen(false);
      void navigate(ROUTES.TOOLS_TAXONOMY_GENERATION_DETAIL(created.id));
    },
    onNameConflict: setCreateConflict,
  });
  const duplicate = useDuplicateTaxonomyDataset();
  /*
   * The campaign mutations invalidate campaigns; the taxonomies they hold are
   * this page's business. A delete detaches, so every affected taxonomy's
   * `campaignId` is stale the moment it returns — without this the rows stay
   * filed under a campaign that is gone until something else refetches.
   */
  const refreshTaxonomies = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
  }, [queryClient]);

  const createCampaign = useCreateTaxonomyCampaign({ onSuccess: refreshTaxonomies });
  const updateCampaign = useUpdateTaxonomyCampaign({
    onSuccess: () => {
      setCampaignEditOpen(false);
      refreshTaxonomies();
    },
  });
  const removeCampaign = useDeleteTaxonomyCampaign({
    onSuccess: () => {
      setCampaignDeleteOpen(false);
      refreshTaxonomies();
    },
  });
  const remove = useDeleteTaxonomyDataset({ onSuccess: () => setDeleteOpen(false) });

  const open = useCallback(
    (id: string) => void navigate(ROUTES.TOOLS_TAXONOMY_GENERATION_DETAIL(id)),
    [navigate],
  );

  const loading = query.isLoading || campaignsQuery.isLoading;
  /*
   * BOTH queries, because a failed campaigns request is the quieter half of the
   * same bug. `groupByCampaign` files a taxonomy whose campaign it cannot find
   * under Ungrouped — the right call for one stale id, the wrong one for a whole
   * request that never landed, and indistinguishable on screen from a workspace
   * that really has no campaigns.
   *
   * `isLoadingError`, NOT `isError`: a query whose REFETCH fails keeps the data
   * it already had, and replacing a good list with an error card is the worse
   * outcome — `refreshTaxonomies` below refetches after every campaign write, so
   * one blip would blank a page whose rename had just succeeded. Same call
   * `EmailTemplatesList` makes, and it also keeps the grouping honest: campaigns
   * only go missing, and taxonomies only get misfiled under Ungrouped, when the
   * campaigns query has no data at all — which is exactly this case.
   */
  const failedToLoad = query.isLoadingError || campaignsQuery.isLoadingError;
  /*
   * "Nothing here yet" means no taxonomies AND no campaigns — a workspace whose
   * taxonomies were all deleted but which still has campaigns is not empty, and
   * showing the create-your-first state over existing rows would be a lie.
   */
  const showEmpty = !loading && !failedToLoad && datasets.length === 0 && campaigns.length === 0;
  const showNoResults = !loading && !failedToLoad && !showEmpty && groups.length === 0;
  const refetching = query.isFetching || campaignsQuery.isFetching || schemasQuery.isFetching;

  /*
   * Refetches the schema list too. It is not part of `failedToLoad` — a schema
   * that will not load costs a LABEL, and every row still renders with
   * "Unknown schema", so blanking the page over it would be the very
   * over-correction this gate exists to avoid. But it leaves the search unable
   * to match on schema, so Retry has to be able to bring it back.
   */
  function retry() {
    void query.refetch();
    void campaignsQuery.refetch();
    void schemasQuery.refetch();
  }

  /** Same words for the `aria-label` and the tooltip, as the icon-only rule requires. */
  function toggleLabel(isOpen: boolean, name: string): string {
    return isOpen
      ? t('taxonomy.campaign.collapseGroup', { name })
      : t('taxonomy.campaign.expandGroup', { name });
  }

  function handleCreateOpenChange(next: boolean) {
    setCreateOpen(next);
    if (!next) {
      setCreateConflict(null);
    }
  }

  return (
    /*
      Mounted here rather than leaning on the one in `main.tsx`: Radix THROWS
      without a provider above it, and the shared test harness does not render
      the app root — so without this every test in this file fails at render.
    */
    <TooltipProvider>
      <ListPageLayout className="page-layout">
        <PageTransition variant="slideUp" delay={0.0}>
          <PageHeader>
            <PageHeaderIdentity>
              <PageHeaderTitle>{t('taxonomy.generationList.title')}</PageHeaderTitle>
            </PageHeaderIdentity>
          </PageHeader>
        </PageTransition>

        <ListPageBody>
          <PageTransition variant="slideUp" delay={0.1} className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center justify-between gap-4 px-section py-4">
              <SearchInput
                className="w-80"
                value={search}
                onChange={setSearch}
                placeholder={t('taxonomy.generationList.searchPlaceholder')}
                aria-label={t('taxonomy.generationList.searchPlaceholder')}
              />
              {canManageTaxonomy && (
                <Button size="default" onClick={() => setCreateOpen(true)}>
                  <PlusIcon className="size-4" />
                  {t('taxonomy.generationList.newTaxonomy')}
                </Button>
              )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-auto px-section pb-section">
              {loading && (
                <div className="flex flex-col gap-xs">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              )}

              {!loading && failedToLoad && (
                <EmptyState
                  variant="outline-dashed"
                  icon={<TriangleAlertIcon className="size-6" />}
                  title={t('taxonomy.generationList.errorTitle')}
                  description={t('taxonomy.generationList.errorDescription')}
                  /*
                   * `status`, not `alert`, though this IS a failure. The shared
                   * problem-details interceptor already raises an assertive
                   * toast per failed request; a second assertive region just
                   * interrupts the first, and a polite one is still announced.
                   */
                  role="status"
                >
                  <Button variant="outline" size="default" onClick={retry} disabled={refetching}>
                    {t('taxonomy.generationList.tryAgain')}
                  </Button>
                </EmptyState>
              )}

              {showEmpty && (
                <EmptyState
                  variant="outline-dashed"
                  icon={<TagsIcon className="size-6" />}
                  title={t('taxonomy.generationList.emptyTitle')}
                  description={t('taxonomy.generationList.emptyDescription')}
                  role="status"
                />
              )}

              {showNoResults && (
                <EmptyState
                  variant="outline-dashed"
                  title={t('taxonomy.generationList.noResultsTitle')}
                  description={t('taxonomy.generationList.noResultsDescription')}
                  role="status"
                />
              )}

              {!loading && !failedToLoad && groups.length > 0 && (
                <Datatable className={redesignTableStyles.table}>
                  <DatatableHeader>
                    <DatatableRow className={ROW_WITHOUT_FOCUS_RING}>
                      {/* The chevron gutter. Header stays on the taxonomy column. */}
                      <DatatableHead scope="col" className="w-10" />
                      <DatatableHead scope="col">
                        {t('taxonomy.generationList.columnTaxonomy')}
                      </DatatableHead>
                      <DatatableHead scope="col" className="w-24">
                        {t('taxonomy.generationList.columnRecords')}
                      </DatatableHead>
                      <DatatableHead scope="col">
                        {t('taxonomy.generationList.columnSchema')}
                      </DatatableHead>
                      <DatatableHead scope="col" className="w-44">
                        {t('taxonomy.generationList.columnUpdated')}
                      </DatatableHead>
                      <DatatableHead scope="col" className="w-52">
                        {t('taxonomy.generationList.columnUpdatedBy')}
                      </DatatableHead>
                      <DatatableHead scope="col" className="w-12" />
                    </DatatableRow>
                  </DatatableHeader>
                  <DatatableBody>
                    {groups.map((group) => {
                      const expanded = isExpanded(group.id);
                      const label = group.campaign?.name ?? t('taxonomy.campaign.ungrouped');

                      return (
                        <Fragment key={group.id}>
                          {/*
                          The row keeps its click as a MOUSE convenience. The keyboard path is
                          the chevron button below, which is what carries `aria-expanded` —
                          the attribute belongs on the control that does the expanding, and on
                          a `<tr>` it means something only inside a `role="grid"`, which this
                          plain table is not. Putting `role="button"`/`tabIndex` on the row
                          instead would override its implicit `row` role and break table
                          semantics for exactly the people this is meant to serve.
                        */}
                          <DatatableRow
                            interactive
                            className={ROW_CLASSES}
                            onClick={() => toggle(group.id, expanded)}
                          >
                            <DatatableCell>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    /*
                                    Stops the row's own handler running after this one. Without
                                    it a chevron click toggles twice and nets no visible change
                                    at all — a silent no-op rather than obvious noise.
                                  */
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggle(group.id, expanded);
                                    }}
                                    aria-expanded={expanded}
                                    aria-label={toggleLabel(expanded, label)}
                                    className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  >
                                    <ChevronRightIcon
                                      aria-hidden
                                      className={`size-4 transition-transform ${
                                        expanded ? 'rotate-90' : ''
                                      }`}
                                    />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{toggleLabel(expanded, label)}</TooltipContent>
                              </Tooltip>
                            </DatatableCell>
                            <DatatableCell>
                              <span className="flex items-center gap-sm">
                                <MegaphoneIcon className="size-4 shrink-0 text-muted-foreground" />
                                <span className="font-medium">{label}</span>
                                <span className="rounded-md bg-secondary px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
                                  {t('taxonomy.campaign.taxonomyCount', {
                                    count: group.totalCount,
                                  })}
                                </span>
                              </span>
                            </DatatableCell>
                            {/* Records, schema, updated and updated-by belong to a
                              taxonomy, not to the group that holds it. */}
                            <DatatableCell />
                            <DatatableCell />
                            <DatatableCell />
                            <DatatableCell />
                            <DatatableCell>
                              {group.campaign && (
                                <CampaignActionMenu
                                  campaign={group.campaign}
                                  canManage={canManageTaxonomy}
                                  onEdit={() => {
                                    setCampaignEditTarget(group.campaign);
                                    setCampaignEditOpen(true);
                                  }}
                                  onDelete={() => {
                                    setCampaignDeleteTarget(group.campaign);
                                    setCampaignDeleteOpen(true);
                                  }}
                                />
                              )}
                            </DatatableCell>
                          </DatatableRow>

                          {expanded &&
                            group.datasets.map((dataset) => (
                              <DatatableRow
                                key={dataset.id}
                                interactive
                                className={ROW_CLASSES}
                                onClick={() => open(dataset.id)}
                              >
                                <DatatableCell />
                                <DatatableCell>
                                  <span className="flex items-start gap-sm pl-6">
                                    <TagsIcon
                                      aria-hidden
                                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                                    />
                                    <span
                                      data-testid="taxonomy-name-cell"
                                      className="flex min-w-0 flex-col"
                                    >
                                      {/*
                                      A real link, not just the row's click: `DatatableRow` is a
                                      bare `<tr>`, so row-click alone left no way in from the
                                      keyboard. Also buys ⌘/middle-click. `stopPropagation`
                                      because react-router's click handler calls `preventDefault`
                                      but NOT `stopPropagation`, so without it the row's handler
                                      navigates a second time.
                                    */}
                                      <Link
                                        to={ROUTES.TOOLS_TAXONOMY_GENERATION_DETAIL(dataset.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                      >
                                        {dataset.name}
                                      </Link>
                                      {dataset.description && (
                                        // `title` because the line is CSS-truncated, the same
                                        // pairing BriefsTableRow and UploadFileRow use.
                                        <span
                                          title={dataset.description}
                                          className="truncate text-xs text-muted-foreground"
                                        >
                                          {dataset.description}
                                        </span>
                                      )}
                                    </span>
                                  </span>
                                </DatatableCell>
                                <DatatableCell>{dataset.recordCount}</DatatableCell>
                                <DatatableCell>{schemaLabel(dataset)}</DatatableCell>
                                <DatatableCell>
                                  {formatRelativeDate(dataset.updatedAt)}
                                </DatatableCell>
                                <DatatableCell>
                                  <PreResolvedAvatarCell
                                    name={
                                      dataset.modifiedBy?.name ??
                                      t('taxonomy.generationList.unknownUser')
                                    }
                                    avatarUrl={dataset.modifiedBy?.avatarUrl ?? null}
                                    truncate
                                    displayNameOverride={
                                      dataset.updatedBy && dataset.updatedBy === currentUserId
                                        ? tCommon('components.ownerCell.me')
                                        : undefined
                                    }
                                  />
                                </DatatableCell>
                                <DatatableCell>
                                  <DatasetActionMenu
                                    dataset={dataset}
                                    canManage={canManageTaxonomy}
                                    duplicating={duplicate.isPending}
                                    onEdit={() => open(dataset.id)}
                                    onDuplicate={() => duplicate.mutate(dataset.id)}
                                    onDelete={() => {
                                      setDeleteTarget(dataset);
                                      setDeleteOpen(true);
                                    }}
                                  />
                                </DatatableCell>
                              </DatatableRow>
                            ))}
                        </Fragment>
                      );
                    })}
                  </DatatableBody>
                </Datatable>
              )}
            </div>
          </PageTransition>
        </ListPageBody>

        <NewTaxonomyModal
          open={createOpen}
          onOpenChange={handleCreateOpenChange}
          onCreate={(payload) => create.mutate(payload)}
          campaigns={campaigns}
          /*
           * The modal asks for a campaign by NAME and gets back an id. Owning the
           * mutation here keeps the modal a form, and keeps the two writes in the
           * order the API needs — campaign first, then the taxonomy that points at
           * it — without the modal knowing either endpoint.
           */
          onCreateCampaign={async (input) => {
            const created = await createCampaign.mutateAsync(input);
            return created.id;
          }}
          existing={datasets}
          submitting={create.isPending}
          serverError={createConflict}
          onClearServerError={() => setCreateConflict(null)}
        />

        {/*
        Mounted unconditionally and driven by `open`, rather than rendered only
        while a row is pending. A dialog UNMOUNTED while still open leaves
        `body { pointer-events: none }` behind and freezes the page — and
        clearing the pending row on success is itself such an unmount, so
        rendering it conditionally reintroduces the trap by a different route.
      */}
        <DeleteConfirmationModal
          open={deleteOpen}
          isDeleting={remove.isPending}
          onCancel={() => setDeleteOpen(false)}
          onConfirmDelete={() => deleteTarget && remove.mutate(deleteTarget.id)}
          resourceType={tCommon('components.deleteConfirmationModal.taxonomy')}
          resourceName={deleteTarget?.name ?? ''}
          warningMessage={t('taxonomy.generationList.deleteBody', {
            count: deleteTarget?.recordCount ?? 0,
          })}
          data-testid="taxonomy-generation-delete-modal"
        />

        <EditCampaignModal
          open={campaignEditOpen}
          onOpenChange={setCampaignEditOpen}
          campaign={campaignEditTarget}
          submitting={updateCampaign.isPending}
          onSave={(changes) =>
            campaignEditTarget && updateCampaign.mutate({ id: campaignEditTarget.id, ...changes })
          }
        />

        {/* Mounted unconditionally for the same reason as the one above. */}
        <DeleteConfirmationModal
          open={campaignDeleteOpen}
          isDeleting={removeCampaign.isPending}
          onCancel={() => setCampaignDeleteOpen(false)}
          onConfirmDelete={() =>
            campaignDeleteTarget && removeCampaign.mutate(campaignDeleteTarget.id)
          }
          resourceType={tCommon('components.deleteConfirmationModal.campaign')}
          resourceName={campaignDeleteTarget?.name ?? ''}
          /*
           * Says the taxonomies SURVIVE. The prototype this UI follows cascades
           * the delete and warns accordingly; ours detaches instead, and copying
           * that warning would frighten people out of an action that is safe.
           */
          warningMessage={t('taxonomy.campaign.deleteWarning', {
            count: datasets.filter((d) => d.campaignId === campaignDeleteTarget?.id).length,
          })}
          data-testid="taxonomy-campaign-delete-modal"
        />
      </ListPageLayout>
    </TooltipProvider>
  );
}
